import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, CheckCircle2, Package, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { TemplateStack, PolicyTemplate } from "@/lib/template-utils";
import { getStackTemplates } from "@/lib/template-utils";
import ReactMarkdown from "react-markdown";

interface StackPreviewDialogProps {
  stack: TemplateStack | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selectedTemplateIds: string[]) => void;
}

export function StackPreviewDialog({
  stack,
  open,
  onOpenChange,
  onConfirm,
}: StackPreviewDialogProps) {
  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [previewTemplate, setPreviewTemplate] = useState<PolicyTemplate | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (stack && open) {
      fetchTemplates();
      // Select all templates by default
      setSelectedTemplates(new Set(stack.templates));
    }
  }, [stack, open]);

  const fetchTemplates = async () => {
    if (!stack) return;

    setLoading(true);
    try {
      const fetchedTemplates = await getStackTemplates(stack);
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTemplate = (templateId: string) => {
    setSelectedTemplates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedTemplates(new Set(templates.map((t) => t.id)));
  };

  const handleDeselectAll = () => {
    setSelectedTemplates(new Set());
  };

  const handleCreate = () => {
    const selectedIds = Array.from(selectedTemplates);
    if (selectedIds.length === 0) {
      toast({
        title: "No templates selected",
        description: "Please select at least one template",
        variant: "destructive",
      });
      return;
    }
    onConfirm(selectedIds);
    onOpenChange(false);
  };

  if (!stack) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {stack.name}
          </DialogTitle>
          <DialogDescription>{stack.description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-[1fr,1fr] gap-4 min-h-0">
          {/* Templates List */}
          <div className="flex flex-col space-y-4 min-h-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedTemplates.size} of {templates.length} selected
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1 pr-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  No templates found
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <Card
                      key={template.id}
                      className={`cursor-pointer transition-all ${
                        previewTemplate?.id === template.id
                          ? "ring-2 ring-primary"
                          : ""
                      }`}
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <CardHeader className="py-3">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedTemplates.has(template.id)}
                            onCheckedChange={() => handleToggleTemplate(template.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <FileText className="h-4 w-4 text-primary" />
                              {template.title}
                            </CardTitle>
                            {template.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {template.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                                    ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Preview Panel */}
          <div className="border rounded-lg p-4 min-h-0 flex flex-col">
            <h3 className="font-semibold mb-3">Preview</h3>
            <ScrollArea className="flex-1">
              {previewTemplate ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2">{previewTemplate.title}</h4>
                    {previewTemplate.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {previewTemplate.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {previewTemplate.category && (
                        <Badge variant="outline" className="text-xs">
                          {previewTemplate.category}
                        </Badge>
                      )}
                      {previewTemplate.compliance_framework && (
                        <Badge variant="secondary" className="text-xs">
                          {previewTemplate.compliance_framework}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>{previewTemplate.content}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  Select a template to preview
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={selectedTemplates.size === 0}>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Create {selectedTemplates.size} Policies as Drafts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
